import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import axios from 'axios';
import { 
  GraduationCap, School, Users, Leaf, Plus, X, Calendar, MapPin, 
  Camera, Trash2, Info, Download, ChevronDown, ChevronUp, FileText,
  Recycle, Sparkles, BookOpen, Edit3, ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { LOGO_BASE64 } from '../assets/logoBase64';
import QuestionnaireForm from './QuestionnaireForm';
import QuestionnaireList from './QuestionnaireList';
import EmmeGuide, { useEmmeGuide, EmmeHelpButton } from './EmmeGuide';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Testo esplicativo MareDu
const MAREDU_INFO = {
  title: "Il Modello Operativo MareDu: Dall'Esperienza alla Consapevolezza",
  intro: "MareDu non è una semplice lezione scolastica, ma un protocollo di Social Innovation che trasforma lo studente da spettatore passivo a \"custode attivo\" del patrimonio marino. Il progetto utilizza l'arte e la narrazione come \"ponti cognitivi\" per rendere tangibile la complessità dei problemi ambientali.",
  sections: [
    {
      title: "1. Fondamenti Metodologici",
      content: "Il cuore del progetto si basa sui pilastri dell'economia circolare: RIDURRE/RIUSO/RICICLO. A differenza dei modelli basati solo sul riciclo industriale, MareDu punta sulla rigenerazione intellettuale e materica, insegnando che il miglior rifiuto è quello che non viene prodotto o quello che trova una nuova vita attraverso la creatività."
    },
    {
      title: "2. Lo Sviluppo in Classe: Le 2 Ore di Immersione",
      content: "Il progetto si sviluppa attraverso un intervento diretto di due ore per ogni classe, condotto dall'artista e autore Massimo Marchiori.",
      subsections: [
        {
          title: "La Narrazione (Il Libro)",
          content: "L'uso del libro \"Il mare parla\" funge da base narrativa. Attraverso le avventure di Rafa, gli studenti esplorano i segreti dei relitti, imparando ad \"ascoltare\" il mare e le sue necessità. Ogni classe riceve una copia del libro come strumento di consultazione permanente."
        },
        {
          title: "L'Esperienza della Materia (Riuso Artistico)",
          content: "Massimo Marchiori porta in classe la plastica recuperata dal mare. Gli studenti non si limitano a guardare, ma comprendono come lo scarto, attraverso il gesto artistico, possa essere nobilitato e trasformato in un messaggio di denuncia. Questo processo rende il problema della plastica \"fisico\" e reale, lontano dalle astrazioni dei libri di testo."
        }
      ]
    },
    {
      title: "3. Integrazione con la Comunità e il Territorio",
      content: "MareDu non finisce tra le mura della scuola, ma si espande verso l'esterno:",
      subsections: [
        {
          title: "Dalla Classe alla Scultura",
          content: "Le riflessioni e l'energia nate durante i laboratori alimentano la creazione delle sculture monumentali realizzate da Stari Ribar con i materiali recuperati (come le reti fantasma). Gli studenti vedono il loro percorso educativo culminare in un'opera pubblica che diventa patrimonio della comunità."
        },
        {
          title: "Citizen Science Attiva",
          content: "Ogni intervento scolastico genera dati sulla consapevolezza e sulla percezione del territorio che vengono inseriti nella dashboard Respiro Blu, rendendo il plesso scolastico un punto di monitoraggio attivo per la \"salute culturale\" del litorale."
        }
      ]
    },
    {
      title: "4. Scalabilità e Impatto (ESG)",
      content: "Il modello MareDu è pensato per essere adottato da aziende e istituzioni tramite il sistema \"Adotta una Scuola\". Ogni intervento è tracciato e certificato. Il successo del metodo, validato su oltre 30.000 studenti, dimostra che il protocollo è pronto per essere scalato in tutto il bacino del Mediterraneo, portando l'eccellenza dell'educazione ambientale veneta a livello europeo."
    }
  ],
  co2_explanation: "Il valore di 1 kg di CO2 risparmiata per studente è calcolato considerando l'impatto educativo: ogni studente sensibilizzato modifica i propri comportamenti quotidiani (riduzione plastica monouso, riciclo corretto, minori sprechi) generando un risparmio medio stimato di 1 kg di CO2 equivalente nel corso dell'anno successivo al laboratorio. Questo valore è supportato da studi sull'impatto dell'educazione ambientale sui comportamenti giovanili."
};

const MareDuSection = () => {
  const { token, isAdmin } = useAuth();
  const { language } = useLanguage();
  const [labs, setLabs] = useState([]);
  const [stats, setStats] = useState({ total_students: 0, total_co2_kg: 0, total_labs: 0, total_schools: 0 });
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [expandedSchools, setExpandedSchools] = useState({});
  const [editingLab, setEditingLab] = useState(null);
  
  // Emme Guide per questa sezione
  const { showGuide, closeGuide, openGuide } = useEmmeGuide('maredu');
  
  // Form state
  const [formData, setFormData] = useState({
    school_name: '',
    city: '',
    province: '',
    class_name: '',
    student_count: '',
    lab_date: new Date().toISOString().split('T')[0],
    photo_url: '',
    sponsor_name: '',
    sponsor_logo: '',
    notes: ''
  });
  
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const photoInputRef = useRef(null);
  const sponsorLogoInputRef = useRef(null);

  const fetchLabs = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/school-labs`);
      setLabs(response.data.labs || []);
      setStats(response.data.stats || {});
      setSchools(response.data.schools || []);
    } catch (error) {
      console.error('Error fetching labs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLabs();
  }, [fetchLabs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.school_name || !formData.city || !formData.class_name || !formData.student_count) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    try {
      if (editingLab) {
        // Update existing lab
        await axios.put(`${API_URL}/api/school-labs/${editingLab}`, {
          ...formData,
          student_count: parseInt(formData.student_count)
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Laboratorio aggiornato!');
        setEditingLab(null);
      } else {
        // Create new lab
        await axios.post(`${API_URL}/api/school-labs`, {
          ...formData,
          student_count: parseInt(formData.student_count)
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Laboratorio aggiunto!');
      }
      
      setShowForm(false);
      setFormData({
        school_name: '',
        city: '',
        province: '',
        class_name: '',
        student_count: '',
        lab_date: new Date().toISOString().split('T')[0],
        photo_url: '',
        sponsor_name: '',
        sponsor_logo: '',
        notes: ''
      });
      fetchLabs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleEdit = (lab) => {
    setFormData({
      school_name: lab.school_name || '',
      city: lab.city || '',
      province: lab.province || '',
      class_name: lab.class_name || '',
      student_count: lab.student_count?.toString() || '',
      lab_date: lab.lab_date || new Date().toISOString().split('T')[0],
      photo_url: lab.photo_url || '',
      sponsor_name: lab.sponsor_name || '',
      sponsor_logo: lab.sponsor_logo || '',
      notes: lab.notes || ''
    });
    setEditingLab(lab.id);
    setShowForm(true);
  };

  const handleDelete = async (labId) => {
    if (!window.confirm('Eliminare questo laboratorio?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/school-labs/${labId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Laboratorio eliminato');
      fetchLabs();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const generateSchoolPDF = async (school) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Genera QR code
    const qrUrl = `https://www.respiroblu.it/maredu/${encodeURIComponent(school.name)}`;
    let qrDataUrl = null;
    try {
      qrDataUrl = await QRCode.toDataURL(qrUrl, { 
        width: 100, 
        margin: 1,
        color: { dark: '#008080', light: '#ffffff' }
      });
    } catch (e) {
      console.error('Error generating QR:', e);
    }
    
    // Header con logo
    if (LOGO_BASE64) {
      doc.addImage(LOGO_BASE64, 'JPEG', 15, 10, 30, 30);
    }
    
    // QR code in alto a destra
    if (qrDataUrl) {
      doc.addImage(qrDataUrl, 'PNG', pageWidth - 40, 10, 25, 25);
    }
    
    // Titolo
    doc.setFontSize(20);
    doc.setTextColor(0, 128, 128);
    doc.text('RESPIRO BLU', pageWidth / 2, 25, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text('Citizen Science per il Mediterraneo', pageWidth / 2, 32, { align: 'center' });
    
    // Linea separatrice
    doc.setDrawColor(0, 128, 128);
    doc.setLineWidth(0.5);
    doc.line(15, 45, pageWidth - 15, 45);
    
    // Certificato
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text('CERTIFICATO LABORATORIO MAREDU', pageWidth / 2, 60, { align: 'center' });
    
    // Info scuola
    doc.setFontSize(14);
    doc.setTextColor(0, 80, 80);
    doc.text(school.name, pageWidth / 2, 75, { align: 'center' });
    
    doc.setFontSize(11);
    doc.setTextColor(80, 80, 80);
    doc.text(`${school.city}${school.province ? ` (${school.province})` : ''}`, pageWidth / 2, 83, { align: 'center' });
    
    // Stats box
    const boxY = 95;
    doc.setFillColor(240, 248, 255);
    doc.roundedRect(30, boxY, pageWidth - 60, 50, 5, 5, 'F');
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text('Impatto Educativo Certificato', pageWidth / 2, boxY + 12, { align: 'center' });
    
    doc.setFontSize(24);
    doc.setTextColor(0, 128, 128);
    doc.text(`${school.total_students}`, pageWidth / 2 - 30, boxY + 32, { align: 'center' });
    doc.text(`${(school.total_students * 1).toFixed(0)} kg`, pageWidth / 2 + 30, boxY + 32, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('Studenti', pageWidth / 2 - 30, boxY + 42, { align: 'center' });
    doc.text('CO₂ Risparmiata', pageWidth / 2 + 30, boxY + 42, { align: 'center' });
    
    // Lista laboratori
    let yPos = boxY + 65;
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text('Laboratori svolti:', 30, yPos);
    
    yPos += 8;
    school.labs.forEach((lab, idx) => {
      if (yPos > 230) {
        doc.addPage();
        yPos = 20;
      }
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      const labDate = new Date(lab.lab_date).toLocaleDateString('it-IT');
      let labText = `• Classe ${lab.class_name} - ${lab.student_count} studenti - ${labDate}`;
      if (lab.sponsor_name) {
        labText += ` (Sponsor: ${lab.sponsor_name})`;
      }
      doc.text(labText, 35, yPos);
      yPos += 6;
    });
    
    // Sezione Sponsor (se presenti)
    if (school.sponsors && school.sponsors.length > 0) {
      yPos += 10;
      if (yPos > 240) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text('Con il supporto di:', 30, yPos);
      yPos += 10;
      
      for (const sponsor of school.sponsors) {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        
        // Prova a caricare il logo sponsor
        if (sponsor.logo) {
          try {
            const logoImg = await loadImageAsBase64(sponsor.logo);
            if (logoImg) {
              doc.addImage(logoImg, 'PNG', 35, yPos - 5, 20, 20);
              doc.setFontSize(10);
              doc.setTextColor(80, 80, 80);
              doc.text(sponsor.name, 60, yPos + 8);
              yPos += 25;
            } else {
              doc.setFontSize(10);
              doc.setTextColor(80, 80, 80);
              doc.text(`• ${sponsor.name}`, 35, yPos);
              yPos += 8;
            }
          } catch (e) {
            doc.setFontSize(10);
            doc.setTextColor(80, 80, 80);
            doc.text(`• ${sponsor.name}`, 35, yPos);
            yPos += 8;
          }
        } else {
          doc.setFontSize(10);
          doc.setTextColor(80, 80, 80);
          doc.text(`• ${sponsor.name}`, 35, yPos);
          yPos += 8;
        }
      }
    }
    
    // Footer
    yPos = Math.max(yPos + 15, 220);
    if (yPos > 270) {
      doc.addPage();
      yPos = 250;
    }
    doc.setFontSize(9);
    doc.setTextColor(128, 128, 128);
    doc.text('Protocollo MareDu - Educazione Ambientale Certificata', pageWidth / 2, yPos, { align: 'center' });
    doc.text(`Generato il ${new Date().toLocaleDateString('it-IT')}`, pageWidth / 2, yPos + 6, { align: 'center' });
    doc.text('www.respiroblu.it', pageWidth / 2, yPos + 12, { align: 'center' });
    
    // Salva
    doc.save(`MareDu_${school.name.replace(/\s+/g, '_')}.pdf`);
    toast.success('PDF generato con QR code!');
  };

  // Helper per caricare immagine da URL come base64
  const loadImageAsBase64 = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } catch (e) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  };

  const toggleSchoolExpand = (schoolKey) => {
    setExpandedSchools(prev => ({
      ...prev,
      [schoolKey]: !prev[schoolKey]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 pb-24">
      {/* Emme Guide */}
      {showGuide && <EmmeGuide sectionId="maredu" onClose={closeGuide} />}
      <EmmeHelpButton onClick={openGuide} />
      
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/30">
          <GraduationCap className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
          <TranslatedText text="LABORATORI MAREDU" />
        </h2>
        <p className="text-emerald-400 mt-1"><TranslatedText text="Educazione Ambientale nelle Scuole" /></p>
        
        {/* Badge Citizen Science */}
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/40 rounded-full">
          <span className="text-sm">🔬</span>
          <span className="text-emerald-300 text-xs font-medium">CITIZEN SCIENCE</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-slate-900/60 border border-emerald-500/30 rounded-xl p-4 text-center">
          <Users className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{stats.total_students?.toLocaleString()}</p>
          <p className="text-xs text-slate-400"><TranslatedText text="Studenti" /></p>
        </div>
        
        <div 
          className="bg-slate-900/60 border border-cyan-500/30 rounded-xl p-4 text-center cursor-pointer hover:border-cyan-500 transition-all"
          onClick={() => setShowInfo(true)}
        >
          <Leaf className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{stats.total_co2_kg?.toLocaleString()} <span className="text-sm font-normal">kg</span></p>
          <p className="text-xs text-slate-400 flex items-center justify-center gap-1">
            <TranslatedText text="CO₂ Risparmiata" />
            <Info className="w-3 h-3 text-cyan-400" />
          </p>
        </div>
        
        <div className="bg-slate-900/60 border border-violet-500/30 rounded-xl p-4 text-center">
          <School className="w-6 h-6 text-violet-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{stats.total_schools}</p>
          <p className="text-xs text-slate-400"><TranslatedText text="Scuole" /></p>
        </div>
        
        <div className="bg-slate-900/60 border border-amber-500/30 rounded-xl p-4 text-center">
          <BookOpen className="w-6 h-6 text-amber-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{stats.total_labs}</p>
          <p className="text-xs text-slate-400"><TranslatedText text="Laboratori" /></p>
        </div>
      </div>

      {/* Pulsante Info */}
      <button
        onClick={() => setShowInfo(true)}
        className="w-full mb-6 py-3 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-xl text-emerald-400 flex items-center justify-center gap-2 transition-all"
      >
        <Info className="w-5 h-5" />
        <TranslatedText text="Scopri il Metodo MareDu" />
      </button>

      {/* Admin: Pulsante Aggiungi */}
      {isAdmin && (
        <button
          onClick={() => setShowForm(true)}
          className="w-full mb-6 py-4 bg-gradient-to-r from-emerald-500 to-cyan-600 hover:from-emerald-600 hover:to-cyan-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg"
        >
          <Plus className="w-5 h-5" />
          <TranslatedText text="Aggiungi Laboratorio" />
        </button>
      )}

      {/* Pulsante Questionario (pubblico) */}
      <button
        onClick={() => setShowQuestionnaire(true)}
        className="w-full mb-6 py-4 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg"
      >
        <ClipboardList className="w-5 h-5" />
        <TranslatedText text="Compila Questionario" />
      </button>

      {/* Admin: Lista Questionari Ricevuti */}
      {isAdmin && (
        <div className="mb-6 bg-slate-900/60 border border-violet-500/30 rounded-xl p-4">
          <QuestionnaireList />
        </div>
      )}

      {/* Lista Scuole */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <School className="w-5 h-5 text-emerald-400" />
          <TranslatedText text="Scuole Coinvolte" />
        </h3>
        
        {schools.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p><TranslatedText text="Nessun laboratorio ancora registrato" /></p>
          </div>
        ) : (
          schools.map((school, idx) => {
            const schoolKey = `${school.name}_${school.city}`;
            const isExpanded = expandedSchools[schoolKey];
            
            return (
              <div key={idx} className="bg-slate-900/60 border border-slate-700/50 rounded-xl overflow-hidden">
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-800/50 transition-all"
                  onClick={() => toggleSchoolExpand(schoolKey)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <School className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{school.name}</p>
                      <p className="text-sm text-slate-400 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {school.city}{school.province && ` (${school.province})`}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-emerald-400 font-bold">{school.total_students} <span className="text-xs font-normal text-slate-400">studenti</span></p>
                      <p className="text-xs text-slate-500">{school.total_labs} laboratori</p>
                    </div>
                    
                    <button
                      onClick={(e) => { e.stopPropagation(); generateSchoolPDF(school); }}
                      className="p-2 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-lg text-cyan-400"
                      title="Scarica PDF"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="border-t border-slate-700/50 p-4 bg-slate-800/30">
                    <p className="text-xs text-slate-500 uppercase mb-2">Dettaglio laboratori</p>
                    <div className="space-y-2">
                      {school.labs.map((lab, labIdx) => (
                        <div key={labIdx} className="flex items-center justify-between py-2 px-3 bg-slate-900/50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-emerald-400 font-medium">Classe {lab.class_name}</span>
                            <span className="text-slate-400">•</span>
                            <span className="text-slate-300">{lab.student_count} studenti</span>
                            {lab.sponsor_name && (
                              <>
                                <span className="text-slate-400">•</span>
                                <span className="text-amber-400 text-sm flex items-center gap-1">
                                  <Sparkles className="w-3 h-3" />
                                  {lab.sponsor_name}
                                </span>
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-500">
                              {new Date(lab.lab_date).toLocaleDateString('it-IT')}
                            </span>
                            {isAdmin && (
                              <>
                                <button
                                  onClick={() => handleEdit({...lab, school_name: school.name, city: school.city, province: school.province})}
                                  className="p-1 text-cyan-400 hover:bg-cyan-500/20 rounded"
                                  title="Modifica"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(lab.id)}
                                  className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                                  title="Elimina"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* CO2 totale scuola */}
                    <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                      <p className="text-emerald-400 text-sm flex items-center gap-2">
                        <Leaf className="w-4 h-4" />
                        <span><TranslatedText text="Impatto ambientale:" /></span>
                        <strong>{school.total_students} kg CO₂ risparmiata</strong>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-emerald-500/50 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-emerald-400">
                {editingLab ? 'Modifica Laboratorio' : 'Nuovo Laboratorio'}
              </h3>
              <button onClick={() => { setShowForm(false); setEditingLab(null); }} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs text-slate-400 uppercase">Nome Scuola *</label>
                <input
                  type="text"
                  value={formData.school_name}
                  onChange={(e) => setFormData({...formData, school_name: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="Es: IC Marco Polo"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400 uppercase">Città *</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                    placeholder="Es: Venezia"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase">Provincia</label>
                  <input
                    type="text"
                    value={formData.province}
                    onChange={(e) => setFormData({...formData, province: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                    placeholder="Es: VE"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400 uppercase">Classe *</label>
                  <input
                    type="text"
                    value={formData.class_name}
                    onChange={(e) => setFormData({...formData, class_name: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                    placeholder="Es: 3A"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase">N° Studenti *</label>
                  <input
                    type="number"
                    value={formData.student_count}
                    onChange={(e) => setFormData({...formData, student_count: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                    placeholder="25"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs text-slate-400 uppercase">Data Laboratorio</label>
                <input
                  type="date"
                  value={formData.lab_date}
                  onChange={(e) => setFormData({...formData, lab_date: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                />
              </div>
              
              <div>
                <label className="text-xs text-slate-400 uppercase">Foto Artwork (opzionale)</label>
                <input
                  type="url"
                  value={formData.photo_url}
                  onChange={(e) => setFormData({...formData, photo_url: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="https://link-alla-foto.jpg"
                />
                <p className="text-xs text-slate-500 mt-1">URL immagine lavori artistici del laboratorio</p>
              </div>

              {/* Sponsor Section */}
              <div className="border border-dashed border-slate-600 rounded-lg p-4 space-y-3">
                <p className="text-xs text-slate-400 uppercase flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  Sponsor (opzionale)
                </p>
                <div>
                  <label className="text-xs text-slate-500">Nome Sponsor</label>
                  <input
                    type="text"
                    value={formData.sponsor_name}
                    onChange={(e) => setFormData({...formData, sponsor_name: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                    placeholder="Es: Azienda Verde Srl"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Logo Sponsor (URL)</label>
                  <input
                    type="url"
                    value={formData.sponsor_logo}
                    onChange={(e) => setFormData({...formData, sponsor_logo: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                    placeholder="https://link-al-logo.png"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs text-slate-400 uppercase">Note (opzionale)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  rows={2}
                  placeholder="Eventuali note..."
                />
              </div>
              
              <button
                type="submit"
                className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg"
              >
                {editingLab ? 'Aggiorna Laboratorio' : 'Salva Laboratorio'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Modal Info MareDu */}
      {showInfo && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-emerald-500/50 rounded-2xl p-6 max-w-2xl w-full my-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-emerald-400">{MAREDU_INFO.title}</h3>
              <button onClick={() => setShowInfo(false)} className="text-slate-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-6 text-slate-300">
              <p className="text-sm leading-relaxed">{MAREDU_INFO.intro}</p>
              
              {MAREDU_INFO.sections.map((section, idx) => (
                <div key={idx}>
                  <h4 className="text-white font-bold mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs">{idx + 1}</span>
                    {section.title.replace(/^\d+\.\s*/, '')}
                  </h4>
                  <p className="text-sm leading-relaxed mb-3">{section.content}</p>
                  
                  {section.subsections && (
                    <div className="ml-4 space-y-3">
                      {section.subsections.map((sub, subIdx) => (
                        <div key={subIdx} className="border-l-2 border-emerald-500/30 pl-4">
                          <p className="text-emerald-400 font-medium text-sm">{sub.title}</p>
                          <p className="text-xs text-slate-400 mt-1">{sub.content}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              {/* Box CO2 */}
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4 mt-6">
                <h4 className="text-cyan-400 font-bold flex items-center gap-2 mb-2">
                  <Leaf className="w-5 h-5" />
                  Come calcoliamo la CO₂ risparmiata?
                </h4>
                <p className="text-xs text-slate-400 leading-relaxed">{MAREDU_INFO.co2_explanation}</p>
              </div>
              
              {/* Badge ESG */}
              <div className="flex flex-wrap gap-2 mt-4">
                <span className="px-3 py-1 bg-emerald-500/20 text-emerald-300 text-xs rounded-full">ESG Certificato</span>
                <span className="px-3 py-1 bg-violet-500/20 text-violet-300 text-xs rounded-full">Social Innovation</span>
                <span className="px-3 py-1 bg-cyan-500/20 text-cyan-300 text-xs rounded-full">Citizen Science</span>
                <span className="px-3 py-1 bg-amber-500/20 text-amber-300 text-xs rounded-full">3R: Riduci Riusa Ricicla</span>
              </div>
            </div>
            
            <button
              onClick={() => setShowInfo(false)}
              className="w-full mt-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg"
            >
              Chiudi
            </button>
          </div>
        </div>
      )}

      {/* Modal Questionario */}
      {showQuestionnaire && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-violet-500/50 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-violet-400 flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                Questionario MareDu
              </h3>
              <button onClick={() => setShowQuestionnaire(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <QuestionnaireForm onClose={() => setShowQuestionnaire(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default MareDuSection;
